import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { Sentry, SentryTeam } from 'app/utils/sentry';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { actionDiff } from 'components/powerFlowShared/helpers/actionDiff';
import { QueuesApi } from 'hocs/queues';
import { history } from 'store';
import { pathWithBasename } from 'utils/pathWithBasename';
import { notificationActions } from '../../notifications/actions/notification';
import { getRequestID } from '../../shared/util/request';
import { addFlow as addFlowAPI, getFlow as getFlowAPI, updateFlow as updateFlowAPI, } from '../api/callFlow';
import { FLOW_ADD_AFTER_BUY_NUMBER, FLOW_ADD_AFTER_BUY_NUMBER_FAILED, FLOW_ADD_AFTER_BUY_NUMBER_SUCCEEDED, FLOW_FETCH, FLOW_FETCH_FAILED, FLOW_FETCH_SUCCEEDED, FLOW_UPDATE, FLOW_UPDATE_FAILED, FLOW_UPDATE_SUCCEEDED, } from '../constants/actionTypes';
const { notificationAdd, notificationRemoveById } = notificationActions;
function* fetchFlow(action) {
    try {
        const data = yield call(getFlowAPI, action.payload.id);
        yield put({ type: FLOW_FETCH_SUCCEEDED, flow: data });
    }
    catch (e) {
        yield put({ type: FLOW_FETCH_FAILED, message: e.message });
    }
}
function* updateFlow(action) {
    var _a, _b;
    const { payload } = action;
    const { flow, initialActions } = payload;
    const { name } = flow;
    try {
        const result = yield call(updateFlowAPI, flow);
        yield put({ type: FLOW_UPDATE_SUCCEEDED, flow: result.data });
        yield put(notificationAdd({
            title: 'Saved',
            text: `"${name}" is saved.`,
            icon: 'i-done',
            className: 'success',
            time: 3000,
            showDesktop: false,
        }));
    }
    catch (e) {
        yield put(notificationAdd({
            className: 'warning',
            icon: 'i-exclamation',
            showDesktop: false,
            text: 'An error occurred when trying to save the call flow',
            title: 'Could not save',
        }));
        Sentry.captureException(e, {
            contexts: {
                response: (_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.data,
                flow: {
                    data: flow,
                    diff: actionDiff(flow.actions, initialActions),
                },
            },
            tags: { team: SentryTeam.Routing },
        });
        yield put({ type: FLOW_UPDATE_FAILED, message: e.message });
    }
}
function* buildTempFlow(action) {
    try {
        let defaultQueue = null;
        let defaultQueueLanguage = 'en-GB';
        const phoneNumber = action.payload.result.data;
        const initNodeId = getRequestID();
        // Get settings (we need the default queue id).
        const getSettings = (state) => state.settings;
        const settingsState = yield select(getSettings);
        // Get default queue by id.
        if (settingsState.organization.organization.default_queue) {
            const queueResult = yield call(QueuesApi.getQueue, settingsState.organization.organization.default_queue);
            if (queueResult === null || queueResult === void 0 ? void 0 : queueResult.data) {
                defaultQueue = queueResult.data;
            }
            if (phoneNumber.e164_format.indexOf('+45') > -1) {
                defaultQueueLanguage = 'da-DK';
            }
        }
        // Build the default flow with the default queue and phoneNumber.
        if (defaultQueue) {
            const defaultFlow = {
                actions: [
                    {
                        badges: [],
                        childIds: [],
                        connectionLabel: 'arrow',
                        icon: null,
                        id: initNodeId,
                        isAction: false,
                        isEditable: false,
                        isRemovable: false,
                        nodeType: FlowNodeTypes.INIT,
                        overlay: [],
                        pageX: 1800,
                        pageY: 700,
                        parentId: null,
                        payload: null,
                        text: 'Incoming Call',
                    },
                    {
                        badges: [],
                        childIds: [],
                        connectionLabel: 'arrow',
                        icon: 'users',
                        id: getRequestID(),
                        isAction: false,
                        isEditable: true,
                        isRemovable: true,
                        nodeType: FlowNodeTypes.QUEUE,
                        overlay: [],
                        pageX: 1800,
                        pageY: 820,
                        payload: {
                            queue: {
                                label: defaultQueue.name,
                                value: defaultQueue.id,
                            },
                            language: defaultQueueLanguage,
                        },
                        parentId: initNodeId,
                        text: defaultQueue.name,
                    },
                ],
                channel: 'pstn_phone',
                active: true,
                location: phoneNumber.e164_format,
                name: `Default call flow for ${phoneNumber.e164_format}`,
            };
            const result = yield call(addFlowAPI, defaultFlow);
            yield put({
                type: FLOW_ADD_AFTER_BUY_NUMBER_SUCCEEDED,
                flow: result.data,
            });
            // Add notification after call flow has been added with goto call flow.
            const notificationID = getRequestID();
            const notificationOptions = {
                id: notificationID,
                title: 'New call flow added',
                text: `We have created a call flow for ${phoneNumber.e164_format} - you can now call it.`,
                className: 'success',
                type: 'callFlowAdded',
                icon: 'i-done-double',
                time: 10000,
                leftAction: {
                    text: 'Dismiss',
                    action: function* () {
                        yield put(notificationRemoveById({ id: notificationID }));
                    },
                },
            };
            yield put(notificationAdd(notificationOptions));
            yield call(history.push, pathWithBasename(`/settings/phone/flow/${result.data.id}`));
        }
    }
    catch (e) {
        console.warn('buildTempFlow exception', e);
        yield put({ type: FLOW_ADD_AFTER_BUY_NUMBER_FAILED, message: e.message });
    }
}
export const callFlow = [
    takeLatest(FLOW_FETCH, fetchFlow),
    takeEvery(FLOW_UPDATE, updateFlow),
    takeEvery(FLOW_ADD_AFTER_BUY_NUMBER, buildTempFlow),
];
